import { useState, useMemo, useCallback } from 'react';
import { Download, ToggleLeftIcon, RefreshCcw as RefreshIcon } from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  ActionBar,
  Button,
  MetricCard,
  Switch,
  BodyText,
  Card,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
  ActionDefinition,
  ColumnDef,
} from '@aaimplatform/ui';
import type { FeatureFlag } from '../api/client';
import {
  usePlatformFeatureFlags,
  useUpdatePlatformFeatureFlags,
} from '../hooks/useFeatureFlags';

function getCategoryColor(category: string | undefined): string {
  if (!category) return 'bg-text-quaternary/10 text-quaternary';
  return 'bg-accent-secondary/10 text-accent-secondary';
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildColumns(
  onToggle: (flag: FeatureFlag) => void,
  pending: Set<string>,
): ColumnDef<FeatureFlag, unknown>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Flag Name',
      cell: ({ row }) => (
        <div>
          <span className="text-sm font-sans font-medium text-primary leading-normal">
            {row.original.name ?? row.original.key}
          </span>
          <div className="text-xs font-mono text-tertiary leading-normal">
            {row.original.key}
          </div>
        </div>
      ),
      size: 280,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ getValue }) => (
        <span className="text-sm font-sans text-secondary leading-normal">
          {(getValue() as string | undefined) ?? '--'}
        </span>
      ),
      size: 280,
    },
    {
      accessorKey: 'enabled',
      header: 'Status',
      cell: ({ row }) => (
        <Switch
          isSelected={row.original.enabled}
          onChange={() => onToggle(row.original)}
          isDisabled={pending.has(row.original.key)}
          size="lg"
        />
      ),
      size: 80,
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => {
        const category = getValue() as string | undefined;
        return (
          <span
            className={`inline-flex px-3 py-1 text-xs font-sans font-semibold rounded-sm leading-normal ${getCategoryColor(category)}`}
          >
            {category ?? 'uncategorized'}
          </span>
        );
      },
      size: 140,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last Modified',
      cell: ({ getValue }) => (
        <span className="text-sm font-sans text-secondary leading-normal">
          {formatDate(getValue() as string | undefined)}
        </span>
      ),
      size: 180,
    },
  ];
}

export function FeatureFlagsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = usePlatformFeatureFlags();
  const updateMutation = useUpdatePlatformFeatureFlags();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [selectedRows, setSelectedRows] = useState<FeatureFlag[]>([]);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

  // Sprint C15-C WP E-4: this page lists PLATFORM-scope flags only.
  // The backend (FeatureFlagService.getPlatformFlags) already filters by
  // scope='PLATFORM' server-side; the client-side filter here is a defensive
  // belt-and-suspenders guard so any TENANT-scope row that leaks through a
  // future endpoint change cannot silently appear on this page. Tenant
  // entitlements (PAL, LOS Preview, PE Valuation, Liquidity Network) are
  // managed per-tenant on the Tenants page and asset class baselines on
  // the Asset Class Defaults page.
  const flags = useMemo<FeatureFlag[]>(
    () => (data ?? []).filter((f) => f.scope === 'PLATFORM'),
    [data],
  );

  const categoryOptions = useMemo(() => {
    const categories = new Set<string>();
    for (const flag of flags) {
      if (flag.category) categories.add(flag.category);
    }
    return Array.from(categories)
      .sort()
      .map((c) => ({ value: c, label: c }));
  }, [flags]);

  const filterDefinitions = useMemo<FilterDefinition[]>(
    () => [
      {
        id: 'enabled',
        label: 'Status',
        type: 'select',
        options: [
          { value: 'true', label: 'Enabled' },
          { value: 'false', label: 'Disabled' },
        ],
      },
      {
        id: 'category',
        label: 'Category',
        type: 'select',
        options: categoryOptions,
      },
    ],
    [categoryOptions],
  );

  const applyUpdates = useCallback(
    (updates: Record<string, boolean>) => {
      if (Object.keys(updates).length === 0) return;
      setPendingKeys((prev) => {
        const next = new Set(prev);
        for (const key of Object.keys(updates)) next.add(key);
        return next;
      });
      updateMutation.mutate(updates, {
        onSettled: () => {
          setPendingKeys((prev) => {
            const next = new Set(prev);
            for (const key of Object.keys(updates)) next.delete(key);
            return next;
          });
        },
        onSuccess: () => {
          setSavedMessage(
            `Updated ${Object.keys(updates).length} flag${Object.keys(updates).length === 1 ? '' : 's'}.`,
          );
          setTimeout(() => setSavedMessage(null), 2500);
        },
      });
    },
    [updateMutation],
  );

  const handleToggle = useCallback(
    (flag: FeatureFlag) => {
      applyUpdates({ [flag.key]: !flag.enabled });
    },
    [applyUpdates],
  );

  const handleBulkEnable = useCallback(() => {
    const updates: Record<string, boolean> = {};
    for (const row of selectedRows) {
      if (!row.enabled) updates[row.key] = true;
    }
    applyUpdates(updates);
    setSelectedRows([]);
  }, [applyUpdates, selectedRows]);

  const handleBulkDisable = useCallback(() => {
    const updates: Record<string, boolean> = {};
    for (const row of selectedRows) {
      if (row.enabled) updates[row.key] = false;
    }
    applyUpdates(updates);
    setSelectedRows([]);
  }, [applyUpdates, selectedRows]);

  const handleBulkExport = useCallback(() => {
    if (selectedRows.length === 0) return;
    const blob = new Blob([JSON.stringify(selectedRows, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `feature-flags-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [selectedRows]);

  const filteredFlags = useMemo(() => {
    let result = [...flags];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (flag) =>
          (flag.name ?? '').toLowerCase().includes(query) ||
          flag.key.toLowerCase().includes(query) ||
          (flag.description ?? '').toLowerCase().includes(query),
      );
    }

    for (const filter of activeFilters) {
      switch (filter.filterId) {
        case 'enabled':
          result = result.filter((f) => String(f.enabled) === filter.value);
          break;
        case 'category':
          result = result.filter((f) => f.category === filter.value);
          break;
      }
    }

    return result;
  }, [flags, searchQuery, activeFilters]);

  const bulkActions = useMemo<ActionDefinition[]>(
    () => [
      {
        id: 'enable',
        label: 'Enable All',
        icon: ToggleLeftIcon,
        variant: 'primary',
        onClick: handleBulkEnable,
      },
      {
        id: 'disable',
        label: 'Disable All',
        icon: ToggleLeftIcon,
        variant: 'secondary',
        onClick: handleBulkDisable,
      },
      {
        id: 'export',
        label: 'Export',
        icon: Download,
        variant: 'secondary',
        onClick: handleBulkExport,
      },
    ],
    [handleBulkEnable, handleBulkDisable, handleBulkExport],
  );

  const enabledCount = useMemo(
    () => filteredFlags.filter((f) => f.enabled).length,
    [filteredFlags],
  );

  const categoryCount = useMemo(() => {
    const categories = new Set<string>();
    for (const flag of filteredFlags) {
      if (flag.category) categories.add(flag.category);
    }
    return categories.size;
  }, [filteredFlags]);

  const columns = useMemo(
    () => buildColumns(handleToggle, pendingKeys),
    [handleToggle, pendingKeys],
  );

  if (isLoading) {
    return (
      <div>
        <Card className="p-8">
          <BodyText variant="ui" className="text-tertiary">
            Loading platform feature flags...
          </BodyText>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <Card className="p-6 bg-error/10 border border-error/20">
          <BodyText variant="ui" className="text-error font-semibold mb-2">
            Unable to load feature flags
          </BodyText>
          <BodyText variant="ui" className="text-error text-sm mb-4">
            {error?.message ?? 'The feature flag endpoint did not respond.'}
          </BodyText>
          <Button variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <BodyText variant="ui" className="text-xs text-tertiary mt-1">
            Aaim platform-wide system flags. These control rollout of new features
            across all tenants. Creation and deletion are handled via migrations.
          </BodyText>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="ghost"
            icon={RefreshIcon}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/*
        Sprint C15-C WP E-4: scope clarifier banner. Tenant entitlements
        are NOT managed here -- they are per-tenant operations on the
        Tenants page. Asset class baselines have their own dedicated
        Asset Class Defaults page.
      */}
      <div className="mb-6 p-4 bg-accent/5 border border-recessed rounded-sm">
        <BodyText variant="ui" className="text-sm text-secondary">
          Tenant entitlements (PAL, LOS Preview, PE Valuation, Liquidity Network)
          are managed per-tenant on the Tenants page. Asset class defaults are
          managed on the Asset Class Defaults page.
        </BodyText>
      </div>

      {savedMessage && (
        <div className="mb-4 p-4 bg-growth/10 border border-growth/30 text-growth rounded-sm">
          <span className="text-sm font-sans">{savedMessage}</span>
        </div>
      )}

      {updateMutation.isError && (
        <div className="mb-4 p-4 bg-error/10 border border-error/30 text-error rounded-sm">
          <span className="text-sm font-sans">
            Failed to update: {updateMutation.error?.message ?? 'unknown error'}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <MetricCard
          label="Total Flags"
          value={filteredFlags.length.toString()}
          change={`${enabledCount} enabled`}
          changeType="positive"
        />
        <MetricCard
          label="Enabled"
          value={enabledCount.toString()}
          change="Active platform-wide"
          changeType="neutral"
        />
        <MetricCard
          label="Categories"
          value={categoryCount.toString()}
          change="Distinct groupings"
          changeType="neutral"
        />
      </div>

      <FilterBar
        filters={filterDefinitions}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search flags by name, key, or description..."
        className="rounded-t-sm"
      />

      <ActionBar
        selectedCount={selectedRows.length}
        totalCount={filteredFlags.length}
        actions={bulkActions}
        onSelectAll={() => setSelectedRows([...filteredFlags])}
        onDeselectAll={() => setSelectedRows([])}
      />

      <DataGrid<FeatureFlag>
        data={filteredFlags}
        columns={columns}
        enableRowSelection
        enableMultiRowSelection
        onSelectionChange={setSelectedRows}
        enablePagination
        defaultPageSize={25}
        enableExport
        enableColumnResize
        hoverable
        striped
        getRowId={(row) => row.key}
        className="rounded-b-sm shadow-sm"
      />
    </div>
  );
}
